
import { deleteThreshold, postNewThreshold, putUpdateThreshold, fetchSensorNames } from "@/helpers/api";
import { defineComponent, toRefs, reactive, computed, ComputedRef, PropType, onMounted, ref } from "vue";
import VueFeather from "vue-feather";
import { LocalThreshold, ThresholdSelector, TagFilter, TagLikeFilter } from "@/interfaces/Threshold";
import { isValidDashboardUid } from "@/helpers/helpers";

export default defineComponent({
  name: "threshold-config-modal",
  components: {
    VueFeather,
  },
  emits: ["clearSelectedThreshold", "savedThreshold", "deletedThreshold"],
  props: {
    selected_threshold: {
      type: Object as PropType<LocalThreshold>,
      required: true,
    },
    sensorTagOptions: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      Name: props.selected_threshold.Name,
      Description: props.selected_threshold.Description,
      Value: props.selected_threshold.Value,
      Severity: props.selected_threshold.Severity,
      Enabled: props.selected_threshold.Enabled,
      DashboardUID: props.selected_threshold.DashboardUID ?? "",
      errorMessage: "",
      showConfirmDeleteDialog: false,

      // Selector broken out into individual editable fields
      sensorNameLike: props.selected_threshold.Selector.sensor_name_like ?? "",
      referenceSuffix: props.selected_threshold.Selector.reference?.name_suffix ?? "",
      referenceMinDays: props.selected_threshold.Selector.reference?.min_history_days ?? 0,
      tagsIn: props.selected_threshold.Selector.tags_in
        ? JSON.parse(JSON.stringify(props.selected_threshold.Selector.tags_in))
        : ({} as TagFilter),
      tagsLike: props.selected_threshold.Selector.tags_like
        ? { ...props.selected_threshold.Selector.tags_like }
        : ({} as TagLikeFilter),

      // UI state for adding new tag filters
      newTagInKey: "",
      newTagInValue: "",
      newTagLikeKey: "",
      newTagLikeValue: "",
    });

    // Provisioned fleet-wide (db/managed_thresholds.sql): viewable here, never editable
    const managed: ComputedRef<boolean> = computed(() => props.selected_threshold.Managed);
    const isRelative: ComputedRef<boolean> = computed(() => state.referenceSuffix.trim() !== "");

    const availableTagInKeys: ComputedRef<string[]> = computed(() =>
      Object.keys(props.sensorTagOptions)
        .filter((k) => !(k in state.tagsIn))
        .sort()
    );

    const availableTagLikeKeys: ComputedRef<string[]> = computed(() =>
      Object.keys(props.sensorTagOptions)
        .filter((k) => !(k in state.tagsLike))
        .sort()
    );

    const tagInValueSuggestions: ComputedRef<string[]> = computed(() => {
      if (!state.newTagInKey) return [];
      return (props.sensorTagOptions[state.newTagInKey] ?? []).filter(
        (v) => !(state.tagsIn[state.newTagInKey] ?? []).includes(v)
      );
    });

    const tagLikeValueSuggestions: ComputedRef<string[]> = computed(
      () => props.sensorTagOptions[state.newTagLikeKey] ?? []
    );

    const addTagIn = () => {
      if (!state.newTagInKey || !state.newTagInValue) return;
      if (!state.tagsIn[state.newTagInKey]) {
        state.tagsIn[state.newTagInKey] = [];
      }
      if (!state.tagsIn[state.newTagInKey].includes(state.newTagInValue)) {
        state.tagsIn[state.newTagInKey].push(state.newTagInValue);
      }
      state.newTagInValue = "";
    };

    const removeTagInValue = (key: string, value: string) => {
      state.tagsIn[key] = state.tagsIn[key].filter((v: string) => v !== value);
      if (state.tagsIn[key].length === 0) {
        delete state.tagsIn[key];
      }
    };

    const addTagLike = () => {
      if (!state.newTagLikeKey || !state.newTagLikeValue) return;
      state.tagsLike[state.newTagLikeKey] = state.newTagLikeValue;
      state.newTagLikeKey = "";
      state.newTagLikeValue = "";
    };

    const removeTagLike = (key: string) => {
      delete state.tagsLike[key];
    };

    const buildSelector = (): ThresholdSelector => {
      const selector: ThresholdSelector = {};
      if (state.sensorNameLike.trim()) {
        selector.sensor_name_like = state.sensorNameLike.trim();
      }
      if (Object.keys(state.tagsIn).length) {
        selector.tags_in = { ...state.tagsIn };
      }
      if (Object.keys(state.tagsLike).length) {
        selector.tags_like = { ...state.tagsLike };
      }
      if (state.referenceSuffix.trim()) {
        selector.reference = {
          name_suffix: state.referenceSuffix,
          min_history_days: Number(state.referenceMinDays) || 0,
        };
      }
      return selector;
    };

    const sensorNameSuggestions = ref<string[]>([]);

    onMounted(async () => {
      try {
        const params = new URLSearchParams();
        params.set("RemoveWaterlines", "true");
        sensorNameSuggestions.value = await fetchSensorNames(params);
      } catch {
        // non-critical, suggestions just won't appear
        console.log("fetchSensorNames failed, no name suggestions will show");
      }
    });

    const clearSelectedThreshold = () => {
      emit("clearSelectedThreshold", null);
    };

    const saveThreshold = async () => {
      state.errorMessage = "";

      if (!state.Name) {
        state.errorMessage = "You must specify a name";
        return;
      }

      if (!state.Value) {
        state.errorMessage = "You must specify a value";
        return;
      }

      const selector = buildSelector();
      if (!selector.sensor_name_like) {
        state.errorMessage = "You must provide a Sensor Name (LIKE)";
        return;
      }
      if (selector.sensor_name_like.length < 5 && selector.sensor_name_like.includes("%")) {
        state.errorMessage = "Sensor Name (LIKE) is very short and contains at least one wildcard, this is too broad.";
        return;
      }

      if (selector.reference) {
        const days = selector.reference.min_history_days ?? 0;
        if (!Number.isInteger(days) || days < 0) {
          state.errorMessage = "Minimum reference history must be a whole number of days, 0 or more";
          return;
        }
      }

      const dashboardUid = state.DashboardUID.trim();
      if (dashboardUid && !isValidDashboardUid(dashboardUid)) {
        state.errorMessage = "Diagnosis dashboard UID must be 1-40 characters of letters, digits, '-' or '_'";
        return;
      }

      const isNew = props.selected_threshold.ThresholdID === null;

      const threshold: LocalThreshold = {
        ThresholdID: props.selected_threshold.ThresholdID,
        Name: state.Name,
        Description: state.Description,
        Selector: selector,
        Value: state.Value,
        Severity: state.Severity,
        CreatedAt: props.selected_threshold.CreatedAt,
        Enabled: state.Enabled,
        DashboardUID: dashboardUid || null,
        Managed: managed.value,
      };

      try {
        let saved: LocalThreshold;
        if (isNew) {
          saved = await postNewThreshold(threshold);
        } else {
          saved = await putUpdateThreshold(threshold);
        }
        emit("savedThreshold", saved);
      } catch (error) {
        if (error instanceof Error) {
          state.errorMessage = `Error saving threshold: ${error.message ?? ""}`;
        }
        return;
      }

      emit("clearSelectedThreshold", null);
    };

    const confirmDelete = () => {
      state.showConfirmDeleteDialog = !state.showConfirmDeleteDialog;
    };

    const callDeleteThreshold = () => {
      try {
        deleteThreshold(props.selected_threshold.ThresholdID!);
        emit("deletedThreshold", props.selected_threshold.ThresholdID!);
        emit("clearSelectedThreshold", null);
      } catch (error) {
        if (error instanceof Error) {
          state.errorMessage = `Error saving threshold: ${error.message ?? ""}`;
        }
        return;
      }
    };

    return {
      ...toRefs(state),
      managed,
      isRelative,
      availableTagInKeys,
      availableTagLikeKeys,
      tagInValueSuggestions,
      tagLikeValueSuggestions,
      addTagIn,
      removeTagInValue,
      addTagLike,
      removeTagLike,
      sensorNameSuggestions,
      clearSelectedThreshold,
      saveThreshold,
      confirmDelete,
      callDeleteThreshold,
    };
  },
});
